import axios, { AxiosError, AxiosResponse } from 'axios'
import { toast } from "sonner"

import type { OfflineAction } from '@redux-offline/redux-offline/lib/types';
import {NetworkError} from '@redux-offline/redux-offline/lib/defaults/effect';

// Create axios instance with base configuration
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
})

// Multipart axios instance — no Content-Type default so browser can set
// multipart/form-data boundary automatically when sending FormData
export const apiClientMultipart = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Accept': 'application/json',
  },
})


// Request interceptor to add auth token
// apiClient.interceptors.request.use(
//   (config) => {
//     const token = localStorage.getItem('current_user')
//     if (token) {
//       config.headers.Authorization = `Bearer ${token}`
//     }
//     return config
//   },
//   (error) => Promise.reject(error)
// )

// Response interceptor to handle 401 errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Clear stored auth token

      // localStorage.removeItem('current_user')

      // Call the unauthorized callback if set
      if (unauthorizedCallback) {
        unauthorizedCallback()
      }
    }

    return Promise.reject(error)
  }
)

// Callback for handling 401 errors
let unauthorizedCallback: (() => void) | null = null

// Function to set the unauthorized callback
export const setUnauthorizedCallback = (callback: () => void) => {
  unauthorizedCallback = callback
}


export const reduxEffect = async (effect: any, _action: OfflineAction): Promise<any> => {

  // apiClient.interceptors.request.use(config => {
  //   store.dispatch(clearNotification());
  //   return config;
  // });

  // Prepare request config with custom headers if provided
  const config: any = {};
  if (effect.headers) {
    config.headers = {...config.headers, ...effect.headers};
  }

  // Resolve the body to send — never mutate effect directly
  let body = effect.body ?? null;

  // Special handling for photo upload trigger - call direct upload function
  if (effect.url === '/trigger-photo-upload' && effect.body && effect.body.temp_id) {
    console.log('📤 API Client: Triggering direct photo upload for:', effect.body.temp_id);

    try {
      const { directPhotoUpload } = await import('@/store/actions/photosActions');
      const result = await directPhotoUpload(effect.body);
      console.log('✅ API Client: Direct photo upload completed');
      return result;
    } catch (error) {
      console.error('❌ API Client: Direct photo upload failed:', error);
      throw error;
    }
  }

  // Feedback submissions — use apiClientMultipart (no Content-Type default) so the browser
  // sets multipart/form-data with the correct boundary when sending FormData
  else if (effect.url === '/update-feedback' || effect.url === '/add-feedback') {
    const { createFeedbackFormData } = await import('@/store/actions/utils');
    const formData = createFeedbackFormData(effect.body);
    return apiClientMultipart.post(effect.url, formData)
      .then(response => response.data)
      .catch(error => {
        if (error.response) {
          if (error.response.data?.msg) toast.error(error.response.data.msg);
          throw new NetworkError(error.response.data || '', error.response.status);
        }
        throw new NetworkError('Could not connect to server', 0);
      });
  }

  // Special handling for file uploads - convert object with File to FormData
  else if (effect.body && typeof effect.body === 'object' && effect.body.file instanceof File) {
    console.log('📤 API Client: Converting file upload object to FormData:', effect.url);

    const formData = new FormData();
    Object.keys(effect.body).forEach(key => {
      if (key === 'file') {
        formData.append('file', effect.body.file, effect.body.file.name);
      } else {
        formData.append(key, effect.body[key]);
      }
    });
    body = formData;
    if (config.headers) delete config.headers['Content-Type'];
  }

  // Legacy FormData handling
  else if (effect.body instanceof FormData) {
    console.log('📤 API Client: Sending FormData request to:', effect.url);
    if (config.headers) delete config.headers['Content-Type'];
  }

  return apiClient[effect.method.toLowerCase()](
    effect.url,
    body,
    config
  )
    .then(response => {
      // console.log("API Response:", response.data);
      // if(response.data.msg) {
      //   store.dispatch(setNotification({type:'success',message:response.data.msg}));
      // }
      return response.data;
    })
    .catch(error => {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response);

        if(error.response.data?.msg) {
          toast.error(error.response.data.msg);
        }

        throw new NetworkError(
          error.response.data || '',
          error.response.status,
        );
        return error.response.data;
      } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        console.log('ERROR REQUEST (NO RESPONSE) :: ', error.request);
        throw new NetworkError(
          'Could not connect to server',
          error.request.status,
        );
        return;
      } else {
        // Something happened in setting up the request that triggered an Error
        throw new NetworkError('Unknown Error Occured', error.request.status);
        console.log('Error', error);
      }

      // Alert.alert(
      // 'Network Error',
      //   error.response.data.message,
      // )
    });
}

export default apiClient